import { useCallback, useEffect, useRef, useState } from "react";
import { PreparingReportLoader } from "@/components/preparing-report-loader";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { ReportPayload } from "@/types";

const REPORT_NATIVE_WIDTH = 816;
const API_BASE = import.meta.env.VITE_API_URL ?? "";

function useContainerScale(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [scale, setScale] = useState(1);

  const update = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const containerWidth = el.clientWidth;
    const ratio = containerWidth / REPORT_NATIVE_WIDTH;
    setScale(ratio >= 0.99 ? 1 : ratio);
  }, [containerRef]);

  useEffect(() => {
    update();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update, containerRef]);

  return scale;
}

export function ReportViewer({
  payload,
  testId,
  token,
  className = "",
  fillHeight = true,
  iframeRef: externalIframeRef,
}: {
  payload: ReportPayload | null;
  testId?: string | null;
  token?: string;
  className?: string;
  fillHeight?: boolean;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
}) {
  const internalIframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeRef = externalIframeRef ?? internalIframeRef;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const scale = useContainerScale(containerRef);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const reportSrc = testId
    ? `${API_BASE}/api/reports/${testId}/view${token ? `?token=${encodeURIComponent(token)}` : ""}`
    : undefined;

  // Reset state when testId changes
  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [testId]);

  const handleIframeLoad = useCallback(() => {
    setLoaded(true);
    const iframe = iframeRef.current;
    if (iframe && payloadRef.current) {
      iframe.contentWindow?.postMessage(
        { type: "cc:report:apply", payload: payloadRef.current },
        "*",
      );
    }
  }, []);

  // v2 postMessage: apply payload when report signals ready
  useEffect(() => {
    if (!reportSrc) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "cc:report:ready" && payloadRef.current) {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "cc:report:apply", payload: payloadRef.current },
          "*",
        );
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [reportSrc]);

  const needsScale = scale < 1;

  if (error) {
    return (
      <section ref={containerRef} className={`flex min-h-0 flex-1 items-center justify-center overflow-auto bg-report ${className}`}>
        <p className="text-sm text-muted">Failed to load report.</p>
      </section>
    );
  }

  if (!testId || !reportSrc) {
    return (
      <section ref={containerRef} className={`flex min-h-0 flex-1 items-center justify-center overflow-auto bg-report ${className}`}>
        <p className="text-sm text-muted">No report available.</p>
      </section>
    );
  }

  return (
    <section
      ref={containerRef}
      className={`relative min-h-0 flex-1 overflow-hidden bg-report ${className}`}
    >
      {!loaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-report/80 backdrop-blur-[2px]">
          <PreparingReportLoader />
        </div>
      )}
      {needsScale ? (
        <iframe
          ref={iframeRef}
          title="Semen analysis report"
          src={reportSrc}
          onLoad={handleIframeLoad}
          onError={() => setError(true)}
          style={{
            width: `${REPORT_NATIVE_WIDTH}px`,
            height: `${Math.round(100 / scale)}%`,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            border: 0,
            display: "block",
          }}
          className="bg-report"
        />
      ) : (
        <iframe
          ref={iframeRef}
          title="Semen analysis report"
          src={reportSrc}
          onLoad={handleIframeLoad}
          onError={() => setError(true)}
          className="mx-auto block h-full border-0 bg-report"
          style={{ width: `${REPORT_NATIVE_WIDTH}px`, maxWidth: "100%" }}
        />
      )}
    </section>
  );
}

export function ReportSheet({
  open,
  onClose,
  payload,
  testId,
  token,
}: {
  open: boolean;
  onClose: () => void;
  payload: ReportPayload | null;
  testId?: string | null;
  token?: string;
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-x-0 top-0 z-50 flex h-dvh flex-col bg-white lg:hidden"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="text-sm font-semibold text-navy">Report</span>
            <button
              type="button"
              onClick={onClose}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-muted hover:bg-surface"
              aria-label="Close report"
            >
              <X className="size-5" />
            </button>
          </div>
          <div data-allow-scroll className="flex min-h-0 flex-1 flex-col overflow-auto">
            <ReportViewer payload={payload} testId={testId} token={token} fillHeight />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
